import { QueryResult } from "@/api/utils/types"
import { useQuery } from "@/api/utils/useQuery"
import { HasuraQueries } from "@/api/hasura/queries"

import dayjs from "dayjs"
import {
  Billing_Account_Bool_Exp,
  DeleteSynchronizedAccountMutation,
  GetAutoSyncedAccountsCountQuery,
  GetJuniferAccountsQuery,
  GetSyncedAccountsNumbersQuery,
  GetUserAccountNumbersQuery,
  Junifer__Account_Bool_Exp,
  Junifer__Account_Order_By,
} from "@/api/hasura/generated/graphql"
import { AccountSyncMutator } from "@/mutators/accountSync/AccountSyncMutator"
import {
  AccountSyncTableFilters,
  FilterAccountStatus,
  SearchTerm,
} from "@/views/admin/syncAccounts/components/syncAccountConfig"
import { QueryAggregator } from "@/api/utils/QueryAggregator"
import { SoDataTableOrderBy } from "@/types/components/SoDataTable"
import { safeNumber } from "@/helpers/objectHelpers"
import { IAccountSyncResult } from "@/models/admin/accountSync/accountSyncResult/accountSyncResult.types"
import { AccountSyncResultParser } from "@/models/admin/accountSync/accountSyncResult/accountSyncResult.parser"
import { getIntersection } from "@/helpers/arrayHelpers"

export type AccountSyncResults = {
  total: number
  items: IAccountSyncResult[]
}

export interface SearchArgs {
  page: number
  pageSize: number
  search: string
  searchTerm: SearchTerm
  orderBy: SoDataTableOrderBy
  filters: AccountSyncTableFilters
  accountNumbers?: string[]
  showSyncedAccounts?: boolean
}

class AccountSyncServiceClass {
  search(args: SearchArgs) {
    args.accountNumbers = undefined

    const filterQueries = new QueryAggregator()
    const queries = new QueryAggregator<AccountSyncResults>()

    let items: IAccountSyncResult[] = []
    let total = 0

    this.filterByBillingAccount(filterQueries, args)
    this.filterByUser(queries, args)

    queries.add(filterQueries.queryAggregate)

    if (!filterQueries.queryAggregate.isFetching) {
      const juniferAccountsQuery = this.getJuniferAccounts(args)
      queries.add(juniferAccountsQuery)
      total = juniferAccountsQuery.data?.total ?? 0
      items = juniferAccountsQuery.data?.items ?? []
    }

    return queries.setData({ total, items }).getAggregate()
  }

  filterByBillingAccount(queries: QueryAggregator, args: SearchArgs) {
    if (!args.showSyncedAccounts) return

    const query = this.getSyncedAccountNumbers(args)
    args.accountNumbers = getIntersection(undefined, query.data)
    queries.add(query)
  }

  filterByUser(queries: QueryAggregator, args: SearchArgs) {
    if (args.searchTerm !== "USER_EMAIL" || !args.search) return

    const query = this.getUserAccountNumbers(args)
    args.accountNumbers = getIntersection(undefined, query.data)
    queries.add(query)
  }

  constructOrderByClause(
    orderBy: SoDataTableOrderBy,
  ): Junifer__Account_Order_By {
    const order: Junifer__Account_Order_By = {}
    for (const property in orderBy) {
      let orderValue: string = orderBy[property]
      if (!orderValue) continue
      orderValue += "_nulls_last"
      const [key, subKey] = property.split(".")
      order[key] = subKey ? { [subKey]: orderValue } : orderValue
    }
    return order
  }

  constructWhereClause(args: SearchArgs): Junifer__Account_Bool_Exp {
    const whereQueries: Junifer__Account_Bool_Exp[] = []
    const status = args.filters.statuses.value
    const isAutoSynced = args.filters.options.value.includes("AUTO_SYNCED")

    this.whereInAccountNumbers(whereQueries, args.accountNumbers)
    this.hasSynchronisedAccounts(whereQueries, isAutoSynced)
    this.whereStatusFilters(whereQueries, status)

    switch (args.searchTerm) {
      case "ACCOUNT_NUMBER":
        this.whereAccountNumber(whereQueries, args.search)
        break
      case "CUSTOMER_NUMBER":
        this.whereCustomerNumber(whereQueries, args.search)
        break
      case "CUSTOMER_NAME":
        this.whereCustomerName(whereQueries, args.search)
        break
      case "JUNIFER_ID":
        this.whereJuniferAccountId(whereQueries, safeNumber(args.search))
        break
    }

    return {
      _and: whereQueries,
    }
  }

  whereStatusFilters(
    whereQueries: Junifer__Account_Bool_Exp[],
    status: FilterAccountStatus | null,
  ) {
    const now = dayjs().startOf("minute").toISOString()
    const farFuture = "9999-01-01T23:59:59+00:00"

    if (status === "CANCELLED") {
      whereQueries.push({
        terminatedDttm: { _is_null: true },
        cancelledDttm: { _lte: now },
      })
    }

    if (status === "ACTIVE") {
      whereQueries.push({
        terminatedDttm: { _is_null: true },
        cancelledDttm: { _is_null: true },
        fromDttm: { _lte: now },
        toDttm: { _gte: now },
      })
    }

    if (status === "TRANSFERRING") {
      whereQueries.push({
        terminatedDttm: { _is_null: true },
        cancelledDttm: { _is_null: true },
        fromDttm: { _gt: now },
        toDttm: { _lt: farFuture },
      })
    }

    if (status === "CLOSED") {
      whereQueries.push({
        _or: [
          {
            cancelledDttm: { _is_null: true },
            fromDttm: { _lt: now },
            toDttm: { _lte: now },
          },
          {
            terminatedDttm: { _lte: now },
          },
        ],
      })
    }

    if (status === "TERMINATING") {
      whereQueries.push({
        terminatedDttm: { _is_null: true },
        cancelledDttm: { _is_null: true },
        fromDttm: { _lt: now },
        toDttm: { _lt: farFuture },
      })
    }
  }

  whereAccountNumber(
    whereQueries: Junifer__Account_Bool_Exp[],
    accountNumber: string,
  ) {
    if (!accountNumber) return
    whereQueries.push({ number: { _like: `%${accountNumber}%` } })
  }

  whereCustomerNumber(
    whereQueries: Junifer__Account_Bool_Exp[],
    customerNumber?: string,
  ) {
    if (!customerNumber) return
    whereQueries.push({
      customer: {
        number: { _like: `%${customerNumber}%` },
      },
    })
  }

  whereCustomerName(
    whereQueries: Junifer__Account_Bool_Exp[],
    customerName?: string,
  ) {
    if (!customerName) return
    whereQueries.push({
      customer: {
        name: { _ilike: `%${customerName}%` },
      },
    })
  }

  whereJuniferAccountId(
    whereQueries: Junifer__Account_Bool_Exp[],
    juniferAccountId?: number,
  ) {
    if (!juniferAccountId) return
    whereQueries.push({ id: { _eq: juniferAccountId } })
  }

  whereInAccountNumbers(
    whereQueries: Junifer__Account_Bool_Exp[],
    accountNumbers?: string[],
  ) {
    if (!accountNumbers) return
    whereQueries.push({ number: { _in: accountNumbers } })
  }

  hasSynchronisedAccounts(
    whereQueries: Junifer__Account_Bool_Exp[],
    value?: boolean,
  ) {
    if (!value) return
    whereQueries.push({ synchronized_account: {} })
  }

  getJuniferAccounts(args: SearchArgs): QueryResult<AccountSyncResults> {
    const where = this.constructWhereClause(args)
    const orderBy = this.constructOrderByClause(args.orderBy)
    const offset = (args.page - 1) * args.pageSize
    const limit = args.pageSize

    return useQuery({
      ...HasuraQueries.syncAccounts.search(offset, limit, where, orderBy),
      useRawResponse: true,
      parserFn: (data: GetJuniferAccountsQuery) => {
        return {
          total: data.total.aggregate?.count ?? 0,
          items: data.accounts.map((account) =>
            AccountSyncResultParser.toDomain(account),
          ),
        }
      },
    })
  }

  getSyncedAccountNumbers(args: SearchArgs): QueryResult<string[]> {
    const searchById = args.searchTerm === "CORE_ID" && !!args.search
    const id = safeNumber(args.search)
    const where: Billing_Account_Bool_Exp = {
      deleted: { _is_null: true },
      ...(searchById ? { id: { _eq: id ?? 0 } } : {}),
    }
    return useQuery({
      ...HasuraQueries.syncAccounts.syncedAccountNumbers(where),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data: GetSyncedAccountsNumbersQuery) =>
        data?.billing_account?.map(({ n }) => n) ?? [],
    })
  }

  getUserAccountNumbers(args: SearchArgs): QueryResult<string[]> {
    const email = `%${args.search}%`
    return useQuery({
      ...HasuraQueries.syncAccounts.userAccountNumbers(email),
      placeholderData: [],
      useRawResponse: true,
      parserFn: (data: GetUserAccountNumbersQuery) => {
        const customerUserRels = data?.users.flatMap(({ r }) => r)
        const accountNumbers = customerUserRels?.flatMap((rel) =>
          rel.c.b.map(({ n }) => n),
        )

        return [...new Set(accountNumbers)] // removes duplicates
      },
    })
  }

  getAutoSyncedAccountsCount() {
    return useQuery({
      ...HasuraQueries.syncAccounts.autoSyncedAccountsCount,
      useRawResponse: true,
      parserFn: (data: GetAutoSyncedAccountsCountQuery) => {
        return data.synchronized_accounts_aggregate.aggregate?.count ?? 0
      },
    })
  }

  async resyncAccounts(juniferAccountIds: number[]) {
    const { mutateAsync } = AccountSyncMutator.resyncAccounts()

    await mutateAsync({ accountIds: juniferAccountIds })
  }

  async autoSyncAccount(accountNumber: string) {
    const { mutateAsync } = AccountSyncMutator.autoSyncAccount()

    await mutateAsync({ juniferAccountNumber: accountNumber })
  }

  async disableAutoSync(autoSyncId: number) {
    const { mutateAsync } = AccountSyncMutator.disableAutoSyncAccount()

    const response = (await mutateAsync({
      id: autoSyncId,
    })) as DeleteSynchronizedAccountMutation

    if (!response.deleted_synchronized_account) {
      throw new Error("Failed to disable account auto-sync")
    }
  }
}

export const AccountSyncService = new AccountSyncServiceClass()
